import { useState, useCallback, useEffect } from "react";
import { Home, BookOpen, LineChart, Menu, User, CheckSquare, Calculator, CreditCard } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { preloadRoutes } from "@/lib/utils";

export default function MobileNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = useCallback((path: string) => {
    if (path === '#') return;
    setIsMenuOpen(false);
    navigate(path);
  }, [navigate]);

  const handlePreload = useCallback((path: string) => {
    switch (path) {
      case '/optimierung':
        preloadRoutes.overview();
        break;
      case '/performance':
        preloadRoutes.development();
        break;
      case '/wertentwicklung':
        preloadRoutes.todos();
        break;
      case '/portfoliopage':
        preloadRoutes.dashboard();
        break;
      case '/profil':
        preloadRoutes.profile();
        break;
      case '/community':
        preloadRoutes.community();
        break;
      case '/akademie':
        preloadRoutes.academy();
        break;
      case '/steuersimulation':
        import('@/components/pages/TaxSimulationPage').catch(() => {});
        break;
      case '/products':
        import('@/components/pages/ProductsPage').catch(() => {});
        break;
    }
  }, []);

  useEffect(() => {
    // Preload likely next routes based on current location
    const timer = setTimeout(() => {
      if (location.pathname === '/optimierung') {
        preloadRoutes.dashboard();
        preloadRoutes.development();
      } else if (location.pathname === '/portfoliopage') {
        preloadRoutes.overview();
        preloadRoutes.profile();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const mainMenuItems = [
    { icon: Home, label: "Übersicht", path: "/optimierung" },
    { icon: BookOpen, label: "Entwicklung", path: "/performance" },
    { icon: CheckSquare, label: "To-Do's", path: "/wertentwicklung" },
    { icon: Menu, label: "Menü", path: "#" },
  ];

  const expandedMenuItems = [
    { icon: Home, label: "Portfolio", path: "/portfoliopage" },
    { icon: User, label: "Profil", path: "/profil" },
    { icon: LineChart, label: "Community", path: "/community" },
    { icon: BookOpen, label: "Akademie", path: "/akademie" },
    { icon: Calculator, label: "Steuersimulation", path: "/steuersimulation" },
    { icon: CreditCard, label: "Premium", path: "/products" },
  ];

  return (
    <>
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-neutral-500/10 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      
      {isMenuOpen && (
        <div className="fixed bottom-[4.5rem] left-4 right-4 bg-white rounded-lg shadow-lg border border-neutral-200/50 p-2 z-50 animate-in slide-in-from-bottom duration-300">
          {expandedMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMenuOpen(false)}
              onMouseEnter={() => handlePreload(item.path)}
              className={cn(
                "flex items-center space-x-3 px-4 py-3.5 rounded-lg transition-colors bg-white",
                location.pathname === item.path 
                  ? "text-blue-600 bg-gradient-to-r from-blue-500/10 to-purple-500/10" 
                  : "text-neutral-600 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-blue-600"
              )}
            >
              <item.icon className={cn(
                "h-5 w-5",
                location.pathname === item.path ? "text-blue-500" : "text-neutral-600"
              )} />
              <span className="font-medium">{item.label}</span>
              {item.label === "Steuersimulation" && (
                <span className="ml-auto text-xs bg-gradient-to-r from-green-400 to-emerald-400 text-white px-2 py-0.5 rounded-full">Neu</span>
              )}
              {item.label === "Premium" && (
                <span className="ml-auto text-xs bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-2 py-0.5 rounded-full">Pro</span>
              )}
            </Link>
          ))}
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-neutral-200/50 lg:hidden">
        <div className="grid grid-cols-4 gap-1 p-1 mx-auto max-w-md">
          {mainMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isMenu = item.label === "Menü";
            return (
              <button
                key={item.path}
                onClick={() => {
                  if (isMenu) {
                    setIsMenuOpen(!isMenuOpen);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                onMouseEnter={() => handlePreload(item.path)}
                className={cn(
                  "flex flex-col items-center justify-center py-2 px-1 relative group transition-all duration-200 rounded-lg bg-white",
                  isActive || (isMenu && isMenuOpen)
                    ? "text-blue-600 bg-gradient-to-r from-blue-500/10 to-purple-500/10" 
                    : "text-neutral-600 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10 hover:text-blue-600"
                )}
              >
                <item.icon className={cn(
                  "h-5 w-5 mb-1 transition-transform duration-200",
                  (isActive || (isMenu && isMenuOpen)) ? "text-blue-600" : "text-neutral-600",
                  isMenu && isMenuOpen && "rotate-90"
                )} />
                <span className={cn(
                  "text-xs font-medium",
                  (isActive || (isMenu && isMenuOpen)) ? "text-blue-600" : "text-neutral-600"
                )}>{item.label}</span>
                {item.label === "Portfolio" && (
                  <span className="absolute -right-1 -top-1 text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white px-1.5 py-0.5 rounded-full">4</span>
                )}
                {(item.label === "Akademie" || item.label === "Community") && (
                  <span className="absolute -right-1 -top-1 text-xs bg-gradient-to-r from-blue-400 to-purple-400 text-white px-1.5 py-0.5 rounded-full">Neu</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}